import { message, Popover } from 'antd';
import Image from 'next/image';

import { DraggerRule } from '@/components/common/Dragger';
import { UseTranslateType } from '@/hooks/useTranslate';
import {
  checkFileDuration,
  checkFileSize,
  getImageResolutionFromFile,
} from '@/utils/fileValidate';

export type VideoUrlData = {
  file: File | null;
  videoUrl: string;
  previewUrl: string;
  format: string;
  resolution: string | number;
  duration: number | string;
};
export type ImageUrlData = {
  file: File | null;
  previewUrl: string;
  format: string;
};
export type UrlData = VideoUrlData | ImageUrlData;

interface DraggerContentProps {
  t: UseTranslateType;
  onChange: (data: UrlData) => void;
  onClear: () => void;
  mode: Exclude<AvatarMode, ''>;
}
export interface DraggerContentRef {
  getData: () => UrlData;
}
import React, { useImperativeHandle, useRef } from 'react';

import { resolutionValidate } from '@/components/common/BatchUpload/common/utils';
import DraggerUpload, {
  DraggerUploadProps,
} from '@/components/UIComponents/DraggerUpload';
import { AIFunction } from '@/utils/const';
import { isCnArea } from '@/utils/envHelper';
import { getVideoCoverUrl, getVideoInfo } from '@/utils/fileUtil';

import DeleteIcon from '../../icon/delete';
import RingIcon from '../../icon/ring';
import TipIcon from '../../icon/tip';
import { VideoPreviewContent } from '../../preview';
import styles from '../../style.module.scss';
import { AvatarMode } from '..';

const DraggerContent = React.forwardRef<DraggerContentRef, DraggerContentProps>(
  (props, ref) => {
    const { t, mode } = props;

    const urlData = useRef<UrlData>({
      ...{
        file: null,
        format: '',
      },
      ...(mode === 'video'
        ? { videoUrl: '', previewUrl: '', duration: '', resolution: '' }
        : { previewUrl: '' }),
    });
    useImperativeHandle(ref, () => ({
      getData: () => {
        return urlData.current;
      },
    }));

    const getRequirements = () => {
      const base = [
        {
          label: t('merror.tip.fileFormats'),
          value: mode === 'video' ? 'MP4/MOV' : 'JPG/JPEG/PNG',
        },
      ];
      if (mode === 'video') {
        base.push({ label: t('merror.faceSwapper.upload.msg.resolution'), value: '4K' });
      } else {
        base.push({ label: t('bc.img.maxSize'), value: '20M' });
      }

      return base;
    };
    const getRules = () => {
      const rules: DraggerRule[] = [
        {
          type: 'accept',
          accept:
            mode === 'video'
              ? ['.mp4', '.mov', 'video/mp4', 'video/quicktime']
              : ['.jpg', '.jpeg', '.png', 'image/jpg', 'image/jpeg', 'image/png'],
          message: t('bc.file.valid.typeError'),
        },
      ];
      const validatorFn = {
        video: async (files: FileList) => {
          try {
            const { isValid: durationValid } = await checkFileDuration(files, {
              min: 6,
            });
            if (!durationValid) {
              message.warning(t('bc.file.valid.durationError'));
              return false;
            }
            const sizeValid = checkFileSize(files, { max: 1024 * 1024 * 1024 * 2 }); //2G
            if (!sizeValid) {
              message.warning(t('bc.file.valid.sizeError'));
              return false;
            }
            const { width, height } = await getVideoInfo(files[0]);
            const resolutionValid = resolutionValidate(`${width}*${height}`, '4k'); //4k
            if (!resolutionValid) {
              message.warning(t('merror.createava.videorequire.resolution'));
              return false;
            }
            return true;
          } catch (error) {
            return false;
          }
        },
        image: async (files: FileList) => {
          try {
            const sizeValid = checkFileSize(files, { max: 1024 * 1024 * 10 }); //10M
            if (!sizeValid) {
              message.warning(t('bc.file.valid.sizeError'));
              return false;
            }
            const { width, height } = await getImageResolutionFromFile(files[0]);
            if (width < 300 || height < 300) {
              message.warning(t('min-resolution') + ': 300x300');
              return false;
            }

            return true;
          } catch (error) {
            return false;
          }
        },
      };
      rules.push({
        type: 'validator',
        validator: validatorFn[mode],
      });

      return rules;
    };
    const VideoReadyDetail = (
      <p className="flex justify-center items-center">
        <span className="font-[400]">
          {t('boom.0.9.voicesModal.upload.limit1')}
          <span className="font-[500]">{t('X-Seconds-Or-More', { num: 6 })}</span>
        </span>
        <span className="pl-[16px] pr-[10px] font-[400]">
          {t('bc.img.maxSize')}
          <span className="font-[500]">2G</span>
        </span>
        <Popover
          classNames={{
            root: styles['create-avatar-modal-popover-tip'],
          }}
          content={
            <div className="py-[12px] leading-[20px] tracking-[0.24px] text-center text-[#f3f3f3]">
              <div className="pb-[6px] text-[14px] ">
                {t('merror.faceSwapper.upload.requirements')}
              </div>
              <div className="px-[20px]">
                {getRequirements().map((item) => {
                  return (
                    <div key={item.label} className="text-[12px]  text-start">
                      <span>{item.label}:&nbsp;</span>
                      <span className=" tracking-[0.5px] leading-[16px] font-[500]">
                        {item.value}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          }
          placement="top"
        >
          <span className="pl-[10px]">
            <TipIcon className="text-common-modal-upload-popover-tip-color" />
          </span>
        </Popover>
      </p>
    );

    const getVideoReadyConfig = () => {
      return {
        tip: `${t('merror.createava.videorequire.people')}, ${t('merror.createava.videorequire.frame')}`,
        detail: VideoReadyDetail,
        onBefore: async (file: any) => {
          try {
            let { duration, width, height } = await getVideoInfo(file);
            urlData.current = {
              ...urlData.current,
              resolution: width + '*' + height,
              duration: duration,
              format: file.type,
            };
            return true;
          } catch (error: any) {
            message.error(t(error));
            return false;
          }
        },
      };
    };
    const getImageReadyConfig = () => {
      return {
        onBefore: async (file: any) => {
          urlData.current = {
            ...urlData.current,
            format: file.type,
          };
          return true;
        },
        slotNode: (ref: React.Ref<HTMLDivElement>) => (
          <div
            ref={ref}
            className="relative flex flex-col justify-center items-center h-[190px] gap-[8px] rounded-[8px]"
          >
            <div
              style={{
                borderRadius: '8px 8px 0 0',
                background:
                  'linear-gradient(0deg, rgba(4, 14, 121, 0.00) -100%, rgba(62, 62, 236, 0.20) 100%)',
              }}
              className=" text-[12px] text-[#636EF4] tracking-[0.32px] w-full h-[22px] flex justify-center items-center absolute top-0 left-0"
            >
              <RingIcon className="text-[#636EF4]" />
              &nbsp; {t('Make-sure-to-upload-a-front-view-portrait-single-p')}
            </div>

            <div className="bg-[#4931FA] w-[30px] h-[30px] rounded-full flex justify-center items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M6.75 4.5L9 2.25M9 2.25L11.25 4.5M9 2.25V9.75M5.25017 7.5C4.55126 7.5 4.2018 7.5 3.92615 7.61418C3.55861 7.76642 3.26642 8.05861 3.11418 8.42615C3 8.7018 3 9.05109 3 9.75V13.35C3 14.1901 3 14.6098 3.16349 14.9307C3.3073 15.2129 3.5366 15.4429 3.81885 15.5867C4.1394 15.75 4.55925 15.75 5.39768 15.75H12.6027C13.4411 15.75 13.8604 15.75 14.1809 15.5867C14.4632 15.4429 14.6929 15.2129 14.8367 14.9307C15 14.6101 15 14.1908 15 13.3523V9.75C15 9.05109 14.9999 8.7018 14.8857 8.42615C14.7335 8.05861 14.4416 7.76642 14.074 7.61418C13.7984 7.5 13.4489 7.5 12.75 7.5"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p className="text-[#11111159] text-[10px] w-[288px] text-center">
              {t('10MB-JPG-PNG-300')}
            </p>
          </div>
        ),
      };
    };
    const getVideoSuccessConfig = () => {
      return {
        onSuccess: async (url: string, file: File, args: { coverUrl?: string }) => {
          (urlData.current as VideoUrlData).videoUrl = url;
          urlData.current.file = file;
          urlData.current.previewUrl =
            args?.coverUrl || (await getVideoCoverUrl(urlData.current.file!));

          props.onChange(urlData.current);
        },
        slotNode(url: string, file: File, onClear: () => void) {
          return (
            <div className="w-full relative">
              <div
                className="absolute right-[10px] top-[10px] cursor-pointer z-[1]"
                onClick={(e) => {
                  e && e.stopPropagation();
                  urlData.current = {
                    file: null,
                    videoUrl: '',
                    previewUrl: '',
                    format: '',
                    resolution: '',
                    duration: '',
                  };
                  onClear();
                  props?.onClear();
                }}
              >
                <DeleteIcon />
              </div>
              <div className="h-[190px]">
                <VideoPreviewContent url={url} />
              </div>
            </div>
          );
        },
      };
    };
    const getImageSuccessConfig = () => {
      return {
        onSuccess: async (url: string, file: File, args: { coverUrl?: string }) => {
          try {
            (urlData.current as ImageUrlData).previewUrl = url;
            urlData.current.file = file;

            props.onChange(urlData.current);
          } catch (error) {
            console.error('图片审核失败', error);
            // throw error;
          }
        },
        slotNode(url: string, file: File, onClear: () => void) {
          return (
            <div className="w-full relative">
              <div
                className="absolute right-[13px] top-[33px] cursor-pointer z-[1] w-[30px] h-[30px] bg-[#fff] border border-[#C5C5C5] rounded-[6px] flex justify-center items-center"
                onClick={(e) => {
                  e && e.stopPropagation();
                  urlData.current = {
                    file: null,
                    previewUrl: '',
                    format: '',
                  };
                  onClear();
                  props?.onClear();
                }}
              >
                <ImageDeleteIcon />
              </div>
              <div className="h-[190px] flex flex-col justify-start items-center gap-[10px]">
                <div
                  className="text-[12px] text-common-text-base-color tracking-[0.32px] w-full h-[22px] flex justify-center items-center"
                  style={{
                    borderRadius: '4px 4px 0 0',
                    background:
                      'linear-gradient(0deg, rgba(4, 14, 121, 0.00) -100%, rgba(62, 62, 236, 0.20) 100%)',
                  }}
                >
                  <RingIcon className="text-common-text-base-color" />
                  &nbsp; {t('Make-sure-to-upload-a-front-view-portrait-single-p')}
                </div>
                <Image
                  src={url}
                  width={0}
                  height={0}
                  style={{
                    width: 'auto',
                    height: '150px',
                    borderRadius: '8px',
                  }}
                  alt=""
                  unoptimized
                />
              </div>
            </div>
          );
        },
      };
    };

    const config: DraggerUploadProps = {
      ...{
        rules: getRules(),
        fileType: mode,

        eventTracking: {
          from: AIFunction.Other,
        },
      },
      ...(mode === 'video'
        ? {
            transcode: true,
            ready: getVideoReadyConfig(),
            success: getVideoSuccessConfig(),
          }
        : {
            imageVerify: isCnArea ? true : false,
            ready: getImageReadyConfig(),
            success: getImageSuccessConfig(),
            height: 190,
          }),
    };

    return <DraggerUpload {...config} />;
  },
);

export default DraggerContent;

const ImageDeleteIcon: React.FC<React.SVGProps<SVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <g opacity="0.6">
      <path
        d="M13 10.5556V15.6111M10 10.5556V15.6111M7 7.66667V16.1889C7 16.9979 7 17.4021 7.16349 17.711C7.3073 17.9828 7.5366 18.2042 7.81885 18.3427C8.1394 18.5 8.55925 18.5 9.39768 18.5H13.6023C14.4408 18.5 14.86 18.5 15.1805 18.3427C15.4628 18.2042 15.6929 17.9828 15.8367 17.711C16 17.4024 16 16.9985 16 16.1911V7.66667M7 7.66667H8.5M7 7.66667H5.5M8.5 7.66667H14.5M8.5 7.66667C8.5 6.99364 8.5 6.65729 8.61418 6.39185C8.76642 6.03792 9.05824 5.75655 9.42578 5.60995C9.70144 5.5 10.0511 5.5 10.75 5.5H12.25C12.9489 5.5 13.2984 5.5 13.574 5.60995C13.9416 5.75655 14.2335 6.03792 14.3857 6.39185C14.4999 6.65729 14.5 6.99364 14.5 7.66667M14.5 7.66667H16M16 7.66667H17.5"
        stroke="#3C3D3E"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);
